import { Link, useLocation } from "react-router-dom";
import {
  Package,
  LogOut,
  X,
  Settings,
  UserCheck,
  Shield,
  Search,
  AlertTriangle,
  Lock,
  Bot,
  MessageCircle,
  TrendingUp,
  Monitor,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  Users,
  ShoppingBasket,
} from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { logOut } from "../services/AuthService";
import { isFreeTier, isUserSetUp } from "../utils/userUtils";
import {
  completeDiscordLink,
  isDiscordLinked,
} from "../services/DiscordService";
import { toast } from "react-toastify";
import Logo from "./Logo";
import SidebarRetailSelector from "./SidebarRetailSelector";
import AnnouncementButton from "./AnnouncementButton";
import GlobalFavoritesButton from "./GlobalFavoritesButton";
import DiscordStatusModal from "./DiscordStatusModal";
import PennyMealsPromo from "./PennyMealsPromo";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useProductContext } from "../context/ProductContext";
import { track } from "../utils/analytics";
import { useRetailerConfig } from "../hooks/useRetailerConfig";
import { useDiscordMembershipStatus } from "../hooks/useDiscordGate";
import { getRetailerConfig, type RetailerType } from "../config/retailers";
import { isBestDealsPath } from "../utils/bestDealsNavigation";

interface SideBarProps {
  isOpen: boolean;
  onClose: () => void;
}

type SideNavItem = {
  path: string;
  label: string;
  icon: typeof Package;
  tourId?: string;
  requireLeads?: boolean;
  requireAlerts?: boolean;
  locked?: boolean;
};

export default function SideBar({ isOpen, onClose }: SideBarProps) {
  const location = useLocation();
  const { user, load } = useUserContext();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const isFreeUser = isFreeTier(user);
  const isBestDealsPage = isBestDealsPath(location.pathname);
  const queryClient = useQueryClient();
  const { selectedProduct } = useProductContext();
  const selectedStoreId = new URLSearchParams(location.search).get("storeId");
  const isOnlineStoreMode = isFreeUser || selectedStoreId === "online";

  const [showDiscordStatusModal, setShowDiscordStatusModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Desktop-only collapsed (thin, icon-only) mode; mobile keeps the overlay drawer.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("dealhawk_sidebar_collapsed") === "1");
  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      localStorage.setItem("dealhawk_sidebar_collapsed", next ? "1" : "0");
      track("sidebar-collapse-toggled", { collapsed: next });
      return next;
    });
  };
  const isActiveUser = !!(user?.retailers && user.retailers.length > 0);
  const { hasIssue: hasDiscordIssue } = useDiscordMembershipStatus();

  useRetailerConfig();
  // Handle Discord linking completion from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const discordLinkSuccess = urlParams.get("discord_link_success");
    const discordUserId = urlParams.get("discord_user_id");
    const discordRefreshToken = urlParams.get("discord_refresh_token");

    if (discordLinkSuccess === "true" && discordUserId && discordRefreshToken) {
      handleCompleteDiscordLink(discordUserId, discordRefreshToken);
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Get retailer config for feature flags
  const retailerConfig = getRetailerConfig(selectedProduct as RetailerType);

  // Navigation items for fully set up users (Saved/Favorites is now handled via ProductsPage filter)
  const allRegularNavItems: SideNavItem[] = [
    {
      path: isOnlineStoreMode
        ? `/${selectedProduct}/products?storeId=online`
        : `/${selectedProduct}/products`,
      label: "Deals",
      icon: Package,
      tourId: "products",
      locked: false,
    },
    ...(selectedProduct === "dollar_general" && !isOnlineStoreMode
      ? [{ path: `/${selectedProduct}/coupons`, label: "Coupon Combos", icon: ShoppingBasket, tourId: undefined }]
      : []),
    { path: isFreeUser ? "/onboarding?intent=local&feature=leads" : `/${selectedProduct}/leads`, label: "Leads", icon: TrendingUp, requireLeads: true, tourId: "leads", locked: isFreeUser },
    { path: isFreeUser ? "/onboarding?intent=local&feature=search" : `/${selectedProduct}/product-search`, label: t("nav.productSearch"), icon: Search, tourId: "search", locked: isFreeUser },
    { path: isFreeUser ? "/onboarding?intent=local&feature=alerts" : `/${selectedProduct}/tracking`, label: "Alerts", icon: Monitor, requireAlerts: true, tourId: "alerts", locked: isFreeUser },
    { path: `/${selectedProduct}/bot-filters`, label: "Change Stores", icon: Bot, locked: false },
  ];

  // Filter nav items based on user permissions and retailer
  const regularNavItems = allRegularNavItems.filter((item) => {
    // For Woot and online-only mode, only show "Store".
    if (isFreeUser && isOnlineStoreMode) {
      return item.path.startsWith(`/${selectedProduct}/products`) || item.locked;
    }
    if (selectedProduct === "woot" || selectedProduct === "kohls" || isOnlineStoreMode) {
      return item.path.startsWith(`/${selectedProduct}/products`);
    }
    // For Best Buy, show Store, Leads, and Search.
    if (selectedProduct === "bestbuy") {
      return (
        item.path.startsWith(`/${selectedProduct}/products`) ||
        item.path === `/${selectedProduct}/leads` ||
        item.path === `/${selectedProduct}/product-search`
      );
    }
    // For Lowe's, show Store and Leads only.
    if (selectedProduct === "lowes") {
      return (
        item.path.startsWith(`/${selectedProduct}/products`) ||
        item.path === `/${selectedProduct}/leads`
      );
    }
    if (item.requireLeads && !retailerConfig.features.leads) return false;
    if (item.requireAlerts && isFreeUser) return false;
    if (item.requireAlerts && !retailerConfig.features.monitors) return false;
    if (item.requireAlerts && (selectedProduct === "target" || selectedProduct === "costco")) return false;
    return true;
  });

  // Check if user has paid access (for HawkAI) - unused while HawkAI is hidden
  // const hasPaidAccess = user?.retailers && user.retailers.length > 0;

  // Navigation items for users who haven't completed setup
  const setupNavItems: SideNavItem[] = [
    { path: "/setup", label: t("setup.title"), icon: UserCheck, tourId: undefined },
    { path: "/contact-us", label: t("emptyStates.help.menuLabel"), icon: MessageCircle, tourId: undefined },
    ...(user?.admin ? [{ path: "/admin/users", label: t("common.admin"), icon: Shield, tourId: undefined }] : []),
  ];

  const handleLogout = () => {
    // Clear React Query cache to prevent hanging loading states
    queryClient.clear();
    // Clear tokens and redirect
    logOut();
  };

  const handleCompleteDiscordLink = async (
    discordUserId: string,
    discordRefreshToken: string
  ) => {
    try {
      await completeDiscordLink({ discordUserId, discordRefreshToken });
      await load(); // Reload user data
      toast.success(t("settings.discordLinkedSuccess"));
      setShowDiscordStatusModal(false);
      // Refresh the page to update the sidebar
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error completing Discord link:", error);
      toast.error(t("settings.discordLinkError"));
    }
  };

  // Discord SVG Icon Component
  const DiscordIcon = ({
    size = 18,
    className = "",
  }: {
    size?: number;
    className?: string;
  }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z" />
    </svg>
  );

  const handleDiscordAction = () => {
    if (!isActiveUser) {
      setShowUpgradeModal(true);
      return;
    }
    setShowDiscordStatusModal(true);
  };

  if (!user) {
    return null;
  }

  const hasFullSidebarAccess = isUserSetUp(user) || isFreeUser;

  // Determine which navigation items to show based on setup status
  const focusedBestDealsNavItems = regularNavItems.filter((item) =>
    item.path.startsWith(`/${selectedProduct}/products`)
  );
  const navItems = hasFullSidebarAccess
    ? isBestDealsPage
      ? focusedBestDealsNavItems
      : regularNavItems
    : setupNavItems;
  const sectionNavItems = [
    ...(!isBestDealsPage && user?.admin
      ? [{ path: "/admin/users", label: t("common.admin"), icon: Shield }]
      : []),
  ];

  return (
    <>
      {/* Mobile backdrop with glassmorphism */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-md z-40 md:hidden transition-all duration-300"
          onPointerDown={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-60 ${collapsed ? "md:w-[72px]" : "md:w-56"} bg-primary-900 dark:bg-dark-900 backdrop-blur-xl border-r border-primary-800/50 dark:border-dark-700/50 shadow-2xl transform transition-all duration-300 ease-out md:relative md:translate-x-0 md:z-30 md:shadow-none md:block ${
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full relative">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600/5 to-transparent pointer-events-none" />

          {/* Mobile close button */}
          <div className="flex items-center justify-between p-4 border-b border-primary-800/50 dark:border-dark-700/50 md:hidden relative z-10">
            <span className="text-base font-semibold text-dark-100">
              {t("nav.menu")}
            </span>
            <button
              onPointerDown={onClose}
              className="text-primary-300 dark:text-dark-400 hover:text-white transition-all duration-200 hover:rotate-90 p-1 rounded-lg hover:bg-primary-800 dark:hover:bg-dark-700"
            >
              <X size={20} />
            </button>
          </div>

          {/* Logo - visible on desktop */}
          <div className={`hidden md:flex items-center border-b border-primary-800/50 dark:border-dark-700/50 relative z-10 ${collapsed ? "flex-col gap-2 px-2 py-3" : "justify-between px-3 py-3"}`}>
            {collapsed ? (
              <Link to="/" onPointerDown={onClose} className="flex items-center justify-center hover:opacity-80 transition-opacity" title="DealHawk">
                <img src="/hawk_icon_white.svg" alt="DealHawk" className="h-8 w-8 object-contain" />
              </Link>
            ) : (
              <Logo
                size="large"
                showText={false}
                variant="light"
                to="/"
                onPointerDown={onClose}
                className="group-hover:drop-shadow-[0_0_8px_rgba(106,13,173,0.4)] transition-all duration-300 group"
              />
            )}
            <button
              onPointerDown={toggleCollapsed}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="flex w-6 h-6 items-center justify-center rounded-md text-primary-300 dark:text-dark-400 hover:text-white hover:bg-primary-800 dark:hover:bg-dark-700 transition-colors"
            >
              {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
            </button>
          </div>

          {/* Retail Selector - visible on desktop */}
          {hasFullSidebarAccess && (
            <>
              <div className="hidden md:block border-b border-primary-800/50 dark:border-dark-700/50 relative z-20">
                <SidebarRetailSelector withMargin={!collapsed} iconOnly={collapsed} />
              </div>
            </>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 md:px-2.5 md:py-3 relative z-10 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const [itemPath, itemQuery = ""] = item.path.split("?");
                const currentQuery = location.search.startsWith("?")
                  ? location.search.slice(1)
                  : location.search;
                const isActive = itemQuery
                  ? location.pathname === itemPath && currentQuery === itemQuery
                  : location.pathname === itemPath;
                return (
                  <li key={`${item.path}-${item.label}-${index}`}>
                    <Link
                      to={item.path}
                      data-tour={item.tourId}
                      title={item.label}
                      onPointerDown={() => {
                        onClose();
                        // Track navigation click in Mixpanel
                        track("navigation-clicked", {
                          tab: item.path,
                          tabLabel: item.label,
                          navigationType: "sidebar",
                          fromPath: location.pathname,
                          selectedProduct,
                        });
                      }}
                      className={`group relative flex items-center gap-3 px-3 py-2.5 md:py-2 rounded-lg text-sm font-medium transition-[colors,background-color,box-shadow,transform] duration-300 ${collapsed ? "md:justify-center md:px-2" : ""} ${
                        isActive
                          ? "bg-primary-600/20 text-primary-400 border border-primary-600/30"
                          : "text-primary-200 dark:text-dark-300 hover:text-white hover:bg-primary-800 dark:hover:bg-dark-700"
                      }`}
                    >
                      {/* Active indicator */}
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-primary-500 rounded-r-full" />
                      )}

                      <div
                        className={`transition-all duration-300 ${
                          isActive
                            ? "text-primary-400"
                            : "group-hover:text-primary-400 group-hover:scale-110"
                        }`}
                      >
                        <Icon size={18} />
                      </div>
                      <span className={`transition-all duration-300 group-hover:translate-x-0.5 text-sm ${collapsed ? "md:hidden" : ""}`}>
                        {item.label}
                      </span>
                      {item.locked && (
                        <span className={`ml-auto inline-flex h-5 items-center gap-1 rounded-full bg-amber-300/15 px-2 text-[9px] font-extrabold uppercase tracking-wide text-amber-200 ${collapsed ? "md:hidden" : ""}`}>
                          <Lock size={9} /> Upgrade
                        </span>
                      )}

                      {/* Hover indicator */}
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary-600/0 to-primary-500/0 group-hover:from-primary-600/10 group-hover:to-primary-500/5 transition-all duration-300 pointer-events-none" />
                    </Link>
                  </li>
                );
              })}
            </ul>
            {sectionNavItems.length > 0 && (
              <div className="mt-4 pt-4 md:mt-3 md:pt-3 border-t border-primary-800/50 dark:border-dark-700/50">
                <ul className="space-y-1">
                  {sectionNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <li key={`section-${item.path}`}>
                        <Link
                          to={item.path}
                          title={item.label}
                          onPointerDown={() => {
                            onClose();
                            track("navigation-clicked", {
                              tab: item.path,
                              tabLabel: item.label,
                              navigationType: "sidebar-section",
                              fromPath: location.pathname,
                              selectedProduct,
                            });
                          }}
                          className={`group relative flex items-center gap-3 px-3 py-2.5 md:py-2 rounded-lg text-sm font-medium transition-[colors,background-color,box-shadow,transform] duration-300 ${collapsed ? "md:justify-center md:px-2" : ""} ${
                            isActive
                              ? "bg-primary-600/20 text-primary-400 border border-primary-600/30"
                              : "text-primary-200 dark:text-dark-300 hover:text-white hover:bg-primary-800 dark:hover:bg-dark-700"
                          }`}
                        >
                          {isActive && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-primary-500 rounded-r-full" />
                          )}
                          <div
                            className={`transition-all duration-300 ${
                              isActive
                                ? "text-primary-400"
                                : "group-hover:text-primary-400 group-hover:scale-110"
                            }`}
                          >
                            <Icon size={18} />
                          </div>
                          <span className={`transition-all duration-300 group-hover:translate-x-0.5 text-sm ${collapsed ? "md:hidden" : ""}`}>
                            {item.label}
                          </span>
                          <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary-600/0 to-primary-500/0 group-hover:from-primary-600/10 group-hover:to-primary-500/5 transition-all duration-300 pointer-events-none" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </nav>

          {/* HawkAI - AI Assistant (hidden for now)
          {hasPaidAccess && (
            <div className="px-3 pb-1 relative z-10">
              <Link
                to="/hawk-ai"
                data-tour="hawk-ai"
                onPointerDown={() => {
                  onClose();
                  track("navigation-clicked", {
                    tab: "/hawk-ai",
                    tabLabel: "HawkAI",
                    navigationType: "sidebar",
                    fromPath: location.pathname,
                    selectedProduct,
                  });
                }}
                className={`group relative flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                  location.pathname === "/hawk-ai"
                    ? "bg-gradient-to-r from-primary-600/30 to-primary-500/20 text-primary-300 border border-primary-500/40"
                    : "text-primary-200 dark:text-dark-300 hover:text-white hover:bg-primary-800 dark:hover:bg-dark-700"
                }`}
              >
                {location.pathname === "/hawk-ai" && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-primary-400 rounded-r-full" />
                )}
                <div className={`transition-all duration-300 ${location.pathname === "/hawk-ai" ? "text-primary-300" : "group-hover:text-primary-400 group-hover:scale-110"}`}>
                  <MessageCircle size={18} />
                </div>
                <span className="transition-all duration-300 group-hover:translate-x-0.5 text-sm">HawkAI</span>
              </Link>
            </div>
          )}
          */}

          {/* Community Success + global favorites */}
          {!isBestDealsPage && (
            <div className={`px-3 pb-1 relative z-10 ${collapsed ? "md:[&_span]:hidden md:[&_a]:justify-center md:[&_button]:justify-center" : ""}`}>
              <Link
                to="/community-successes"
                title="Community Success"
                onPointerDown={onClose}
                className={`group relative flex items-center gap-3 w-full px-3 py-2.5 md:py-2 rounded-lg text-sm font-medium transition-[colors,background-color,box-shadow,transform] duration-300 ${collapsed ? "md:justify-center md:px-2" : ""} ${
                  location.pathname === "/community-successes"
                    ? "bg-primary-600/20 text-primary-400 border border-primary-600/30"
                    : "text-primary-200 dark:text-dark-300 hover:text-white hover:bg-primary-800 dark:hover:bg-dark-700"
                }`}
              >
                {location.pathname === "/community-successes" && (
                  <div className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r-full bg-primary-500" />
                )}
                <Users size={18} className={location.pathname === "/community-successes" ? "text-primary-400" : "group-hover:text-primary-400"} />
                <span className={`text-sm ${collapsed ? "md:hidden" : ""}`}>Community Success</span>
              </Link>
              {!isFreeUser && <GlobalFavoritesButton iconSize={18} variant="sidebar" />}
            </div>
          )}

          {/* Announcements */}
          {!isBestDealsPage && user.retailers && user.retailers.length > 0 && (
            <div className={`px-3 pb-1 relative z-10 ${collapsed ? "md:[&_span]:hidden md:[&_a]:justify-center md:[&_button]:justify-center" : ""}`}>
              <AnnouncementButton iconSize={18} variant="sidebar" />
            </div>
          )}

          {/* Discord Section */}
          {!isBestDealsPage && (
            <div className="px-3 pb-3 relative z-10">
              <button
                onPointerDown={() => {
                  const discordLinked = isDiscordLinked(user);
                  track("navigation-clicked", {
                    tab: "discord",
                    tabLabel: discordLinked ? t("nav.discord") : t("nav.linkDiscord"),
                    navigationType: "sidebar",
                    action: discordLinked ? "open-discord" : "link-discord",
                    isLinked: discordLinked,
                    fromPath: location.pathname,
                  });
                  handleDiscordAction();
                }}
                className={`group relative flex items-center gap-3 w-full px-3 py-2.5 md:py-2 rounded-lg text-sm font-medium transition-[colors,background-color,box-shadow,transform] duration-300 text-primary-200 dark:text-dark-300 hover:text-white hover:bg-primary-800 dark:hover:bg-dark-700 ${collapsed ? "md:justify-center md:px-2" : ""}`}
              >
                <div className="transition-all duration-300 group-hover:text-primary-400 group-hover:scale-110">
                  <DiscordIcon size={18} />
                </div>
                <span className={`transition-all duration-300 group-hover:translate-x-0.5 text-sm ${collapsed ? "md:hidden" : ""}`}>
                  {isDiscordLinked(user) ? t("nav.discord") : t("nav.linkDiscord")}
                </span>
                {hasDiscordIssue && (
                  <span
                    className={`ml-auto h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-primary-900 dark:ring-dark-900 shadow-[0_0_10px_rgba(251,191,36,0.7)] ${collapsed ? "md:absolute md:top-1.5 md:right-1.5 md:ml-0" : ""}`}
                    aria-label="Discord needs attention"
                  />
                )}
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary-600/0 to-primary-500/0 group-hover:from-primary-600/10 group-hover:to-primary-500/5 transition-all duration-300 pointer-events-none" />
              </button>
            </div>
          )}

          {!isBestDealsPage && (
            <PennyMealsPromo variant="sidebar" isMember={!isFreeUser} collapsed={collapsed} fromPath={location.pathname} />
          )}

          {/* Bottom section - Theme, Settings (only for set up users) and Logout */}
          <div className="p-3 md:p-2 border-t border-primary-800/50 dark:border-dark-700/50 relative z-10 bg-primary-800/40 dark:bg-dark-800/40 backdrop-blur-sm">
            {/* Theme Toggle */}
            <button
              onPointerDown={() => {
                toggleTheme();
                track("theme-toggled", { newTheme: theme === "dark" ? "light" : "dark" });
              }}
              className={`group relative flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-primary-200 dark:text-dark-300 outline-none hover:text-white hover:bg-primary-800 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-300 dark:hover:bg-dark-700 transition-all duration-300 mb-1 ${collapsed ? "md:justify-center md:px-2" : ""}`}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
            >
              <div className="transition-all duration-300 group-hover:text-primary-400 group-hover:scale-110">
                {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
              </div>
              <span className={`transition-all duration-300 group-hover:translate-x-0.5 text-sm ${collapsed ? "md:hidden" : ""}`}>
                {theme === "dark" ? "Light" : "Dark"}
              </span>
            </button>
            <Link
              to="/contact-us"
              onPointerDown={() => {
                onClose();
                track("navigation-clicked", {
                  tab: "/contact-us",
                  tabLabel: t("emptyStates.help.menuLabel"),
                  navigationType: "sidebar",
                  fromPath: location.pathname,
                  selectedProduct,
                });
              }}
              title={t("emptyStates.help.menuLabel")}
              className={`group relative flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-[colors,background-color,box-shadow,transform] duration-300 mb-1 ${collapsed ? "md:justify-center md:px-2" : ""} ${
                location.pathname === "/contact-us"
                  ? "bg-primary-600/20 text-primary-400 border border-primary-600/30"
                  : "text-primary-200 dark:text-dark-300 hover:text-white hover:bg-primary-800 dark:hover:bg-dark-700"
              }`}
            >
              {location.pathname === "/contact-us" && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-500 rounded-r-full" />
              )}
              <div className={`transition-all duration-300 ${location.pathname === "/contact-us" ? "text-primary-400" : "group-hover:text-primary-400 group-hover:scale-110"}`}>
                <MessageCircle size={16} />
              </div>
              <span className={`transition-all duration-300 group-hover:translate-x-0.5 text-sm ${collapsed ? "md:hidden" : ""}`}>
                {t("emptyStates.help.menuLabel")}
              </span>
            </Link>
            {hasFullSidebarAccess && (
              <Link
                to="/settings"
                onPointerDown={() => {
                  onClose();
                  track("navigation-clicked", {
                    tab: "/settings",
                    tabLabel: t("common.settings"),
                    navigationType: "sidebar",
                    fromPath: location.pathname,
                    selectedProduct,
                  });
                }}
                title={t("common.settings")}
                className={`group relative flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-[colors,background-color,box-shadow,transform] duration-300 mb-1 ${collapsed ? "md:justify-center md:px-2" : ""} ${
                  location.pathname === "/settings"
                    ? "bg-primary-600/20 text-primary-400 border border-primary-600/30"
                    : "text-primary-200 dark:text-dark-300 hover:text-white hover:bg-primary-800 dark:hover:bg-dark-700"
                }`}
              >
                {location.pathname === "/settings" && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-500 rounded-r-full" />
                )}
                <div className={`transition-all duration-300 ${location.pathname === "/settings" ? "text-primary-400" : "group-hover:text-primary-400 group-hover:scale-110"}`}>
                  <Settings size={16} />
                </div>
                <span className={`transition-all duration-300 group-hover:translate-x-0.5 text-sm ${collapsed ? "md:hidden" : ""}`}>
                  {t("common.settings")}
                </span>
              </Link>
            )}
            <button
              onPointerDown={handleLogout}
              title={t("common.logout")}
              className={`group relative flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-dark-300 hover:text-red-400 hover:bg-red-950/30 transition-all duration-300 ${collapsed ? "md:justify-center md:px-2" : ""}`}
            >
              <div className="transition-all duration-300 group-hover:text-red-400 group-hover:scale-110">
                <LogOut size={16} />
              </div>
              <span className={`transition-all duration-300 group-hover:translate-x-0.5 text-sm ${collapsed ? "md:hidden" : ""}`}>
                {t("common.logout")}
              </span>
            </button>
          </div>
        </div>
      </aside>

      {/* Upgrade Required Modal (non-active users) */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Lock size={24} className="text-primary-600 dark:text-primary-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Upgrade Required
                </h2>
              </div>
              <button
                onPointerDown={() => setShowUpgradeModal(false)}
                className="text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Discord access is available for active subscribers. Subscribe to a plan to unlock the DealHawk Discord community.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onPointerDown={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <Link
                  to="/setup"
                  onPointerDown={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg font-medium text-sm bg-primary-600 text-white hover:bg-primary-700 transition-colors text-center"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <DiscordStatusModal
        isOpen={showDiscordStatusModal}
        onClose={() => setShowDiscordStatusModal(false)}
      />
    </>
  );
}
